import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--st-cream)] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="block font-heading uppercase tracking-[0.18em] text-xs text-[var(--st-fg)]/60 hover:text-[var(--st-maroon)] mb-6"
          data-testid="account-login-home-link"
        >
          ← Substitute Teacher
        </Link>
        <div className="bg-white border-2 border-[#111] shadow-brutal p-8">
          <h1 className="font-heading uppercase text-3xl text-[var(--st-fg)] mb-1">
            Sign in
          </h1>
          <p className="text-sm text-[var(--st-fg)]/70 mb-6">
            Track orders, manage addresses, save favourites.
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
                data-testid="account-login-email"
              />
            </div>
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
                Password
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
                data-testid="account-login-password"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-700 bg-red-100 border-2 border-red-700 px-3 py-2"
                data-testid="account-login-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="account-login-submit"
              className="w-full font-display uppercase tracking-tight text-base py-3 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] transition-colors shadow-brutal-sm disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-sm text-[var(--st-fg)]/70">
            New here?{" "}
            <Link
              to="/account/signup"
              className="text-[var(--st-maroon)] underline"
              data-testid="account-login-to-signup"
            >
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
